import { Check, TriangleAlert } from "lucide-react";
import React, { Dispatch, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgCheckbox } from "@intelligems/ig-theme";
import {
  StripeApiCancelSubscriptionResponseBodyType,
  StripeApiCreateSubscriptionActionType,
  StripeApiCreateSubscriptionResponseBodyType,
  StripeApiModifySubscriptionByIdActionType,
  StripeApiModifySubscriptionByIdResponseBodyType,
} from "@intelligems/ig-types";
import { FormControlLabel, Grid, useTheme } from "@mui/material";
import { adminKeys } from "@src/api/queryKeys";
import { usePostStripeApi } from "@src/api/stripe/stripe-api";
import { AreYouSure } from "@src/components/Popover/AreYouSure";
import { openStripe } from "@src/features/admin/components/utils";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";
import { useQueryClient } from "@tanstack/react-query";

interface SaveRowProps {
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
}

export const SaveRow = ({ payload, dispatchPayload }: SaveRowProps) => {
  const createDrawer = useSelector(getDrawer("createSubscription"));
  const modifyDrawer = useSelector(getDrawer("modifySubscription"));
  const [acknowledge, setAcknowledge] = useImmer<boolean | null>(null);
  const drawer = createDrawer || modifyDrawer;
  const theme = useTheme();
  const [autoOpen, setAutoOpen] = useImmer<boolean>(true);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const createSubscription =
    usePostStripeApi<StripeApiCreateSubscriptionResponseBodyType>();
  const modifySubscription =
    usePostStripeApi<StripeApiModifySubscriptionByIdResponseBodyType>();
  const cancelSubscription =
    usePostStripeApi<StripeApiCancelSubscriptionResponseBodyType>();

  useEffect(() => {
    if (Object.keys(payload.warnings).length > 0) {
      if (acknowledge === null) {
        setAcknowledge(false);
      }
    } else {
      setAcknowledge(null);
    }
  }, [payload.warnings, payload.billingProvider]);

  if (drawer === undefined) {
    return null;
  }

  const onModifySubscription = () => {
    if (!payload.customer) {
      return;
    }

    if (payload.mode !== "modify") {
      return;
    }

    modifySubscription.mutate(
      {
        action: "modifySubscriptionById",
        orgs: payload.orgs,
        stripeSubscriptionId: payload.stripeSubscriptionId,
        subscriptionLinkIds: payload.subscriptionLinkIds,
        customerId: payload.customer.id,
        phases: payload.phases.map((phase) => ({
          ...phase,
          couponId: phase.coupon?.id || undefined,
          items: phase.items.map((item) => ({
            priceId: item.price!.id,
            quantity: item.quantity,
            productId: item.product.id,
            id: item.id,
            deleted: item.deleted,
            version: item.product.version,
          })),
        })),
        settings: {
          autoScale: payload.settings.autoScale,
          collectionMethod: payload.settings.collectionMethod,
          prorationBehavior: payload.settings.prorationBehavior,
        },
      } as StripeApiModifySubscriptionByIdActionType,
      {
        onSuccess: (data) => {
          dispatchPayload({ type: "saveCompleted" });
          dispatch(closeDrawer("createSubscription"));
          dispatch(closeDrawer("modifySubscription"));
          if (autoOpen) {
            openStripe(`subscriptions/${data.subscription.id}`);
          }
        },
      }
    );
  };

  const onCreateSubscription = () => {
    if (!payload.customer) {
      return;
    }

    const phase = payload.phases[0];

    createSubscription.mutate(
      {
        orgs: payload.orgs,
        action: "createSubscription",
        billingProvider: payload.billingProvider,
        customerId: payload.customer.id,
        phases: [
          {
            phaseStart: 0,
            couponId: phase.coupon?.id,
            items: phase.items.map((item) => ({
              priceId: item.price!.id,
              quantity: item.quantity,
              productId: item.product.id,
              version: item.product.version,
            })),
            trialEndTs: phase.trialEndTs,
          },
        ],
        settings: {
          autoScale: payload.settings.autoScale,
          collectionMethod: payload.settings.collectionMethod,
          prorationBehavior: payload.settings.prorationBehavior,
        },
      } as StripeApiCreateSubscriptionActionType,
      {
        onSuccess: async (data) => {
          dispatchPayload({ type: "saveCompleted" });
          dispatch(closeDrawer("createSubscription"));
          if (autoOpen) {
            openStripe(`subscriptions/${data.subscription.id}`);
          }

          if (drawer?.agencyId) {
            await queryClient.invalidateQueries({
              queryKey: adminKeys.agency(drawer?.agencyId),
            });
          }
        },
      }
    );
  };

  const onCancelSubscription = () => {
    if (!payload.customer) {
      return;
    }

    if (payload.mode !== "modify") {
      return;
    }

    cancelSubscription.mutate(
      {
        orgs: payload.orgs,
        action: "cancelSubscriptionById",
        stripeSubscriptionId: payload.stripeSubscriptionId,
      },
      {
        onSuccess: async () => {
          dispatch(closeDrawer("createSubscription"));
          dispatch(closeDrawer("modifySubscription"));

          if (drawer?.agencyId) {
            await queryClient.invalidateQueries({
              queryKey: adminKeys.agency(drawer?.agencyId),
            });
          }
        },
      }
    );
  };

  const loading =
    createSubscription.isPending ||
    modifySubscription.isPending ||
    cancelSubscription.isPending;

  return (
    <Grid container item justifyContent={"space-between"} alignItems={"center"}>
      <Grid item>
        {payload.mode === "modify" &&
        !(
          payload.cancelAt && payload.cancelAt >= Math.floor(Date.now() / 1000)
        ) ? (
          <AreYouSure
            keyId={"cancel-sub"}
            yesMessage={"Cancel Subscription"}
            enabled={true}
            isLoading={false}
            onYesClick={() => onCancelSubscription()}
          >
            <IgButton
              color={"error"}
              sx={{
                marginRight: "10px",
              }}
              disabled={payload.status === "canceled"}
              loading={loading}
            >
              Cancel Subscription
            </IgButton>
          </AreYouSure>
        ) : null}
        {Object.keys(payload.warnings).length > 0 ? (
          <IgButton
            sx={{
              fontWeight: 600,
            }}
            onClick={() => setAcknowledge(true)}
            startIcon={
              acknowledge ? (
                <Check size={16} color={theme.igIconColors.dark} />
              ) : (
                <TriangleAlert size={16} color={theme.igIconColors.dark} />
              )
            }
          >
            {acknowledge ? "Warnings Acknowledged" : "Acknowledge Warnings"}
          </IgButton>
        ) : null}
      </Grid>
      <Grid item>
        <Grid container item>
          <Grid item>
            <FormControlLabel
              control={
                <IgCheckbox
                  checked={autoOpen}
                  disabled={loading}
                  onChange={() => setAutoOpen((draft) => !draft)}
                />
              }
              disabled={loading || payload.status === "canceled"}
              label={"Open subscription in Stripe"}
            />
          </Grid>
          <Grid item>
            <IgButton
              loading={loading}
              onClick={
                payload.mode === "create"
                  ? onCreateSubscription
                  : onModifySubscription
              }
              disabled={
                (Object.keys(payload.warnings).length > 0 && !acknowledge) ||
                payload.status === "canceled" ||
                !payload.phases.every((phase) =>
                  phase.items.every(
                    (item) => item.product.id && item.price?.id && item.quantity
                  )
                )
              }
            >
              Save
            </IgButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
